import { type FC, useState } from "react";
import { Autocomplete, Link, Stack, TextField } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  useEmailTemplateQuery,
  useEmailTemplatesListQuery,
  type EmailTemplatePickerProps,
} from "@/partner/entities/marketing";
import { PagePaths } from "@/partner/shared/constants";
import { useDebounce } from "@/shared/hooks";

const SEARCH_DEBOUNCE_MS = 300;

interface EmailOption {
  id: string;
  name: string;
}

/** Шаблон письма из библиотеки: поиск по названию, ссылка «Открыть» в новой вкладке. */
export const EmailTemplateAutocomplete: FC<EmailTemplatePickerProps> = ({
  value,
  onChange,
  disabled,
  label,
  error,
  helperText,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const list = useEmailTemplatesListQuery({
    variables: {
      search: debouncedSearch || undefined,
      page: 0,
      rowsPerPage: 20,
    },
    fetchPolicy: "cache-and-network",
  });
  // Выбранного шаблона может не быть на первой странице поиска: подпись отдельным запросом по id.
  const selected = useEmailTemplateQuery({
    variables: { id: String(value) },
    skip: !value,
  });
  const options: EmailOption[] = (list.data?.emailTemplates.nodes ?? []).map(
    (node) => ({
      id: node.id,
      name: node.name,
    })
  );
  const current: EmailOption | null = value
    ? (options.find((o) => o.id === value) ?? {
        id: value,
        name: selected.data?.emailTemplate.name ?? "",
      })
    : null;

  return (
    <Stack spacing={1}>
      <Autocomplete<EmailOption>
        fullWidth
        disabled={disabled}
        loading={list.loading}
        options={options}
        filterOptions={(opts) => opts}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, other) => option.id === other.id}
        value={current}
        onInputChange={(_event, input, reason) =>
          setSearch(reason === "input" ? input : "")
        }
        onChange={(_event, option) =>
          onChange(option?.id ?? null, option?.name ?? null)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
          />
        )}
      />
      {value && (
        <Link
          component={RouterLink}
          to={`${PagePaths.emailTemplates}?tab=edit&id=${value}`}
          target="_blank"
          rel="noopener"
          variant="body2"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            alignSelf: "flex-start",
            gap: 0.5,
          }}
        >
          {t("broadcasts.form.openEmail")}
          <OpenInNewIcon fontSize="inherit" />
        </Link>
      )}
    </Stack>
  );
};
